import { useCallback, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import Utilities from "../../dependencies/custom/react-utilities";
import Settings from "../../dependencies/custom/settings";

const listFrom = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const query = (params = {}) => {
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    ),
  );
  return new URLSearchParams(cleaned).toString();
};

const detailUrl = (template, id) => template.replace(":id", id);

const useBursaryGiants = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const request = useCallback(
    async (method, url, data, successMessage) => {
      setLoading(true);
      try {
        const response = await Utilities.fetchWithAuth(
          method,
          url,
          data,
          navigate,
        );
        if (response?.ok) {
          if (successMessage) message.success(successMessage);
          return response.data ?? true;
        }
        Utilities.showError(
          response,
          response?.data?.detail || response?.msg || "Request failed",
        );
        return null;
      } catch (error) {
        console.error(error);
        message.error("Request failed");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const fetchList = useCallback(
    async (url, params = {}) => {
      const qs = query(params);
      const data = await request("GET", `${url}${qs ? `?${qs}` : ""}`, null);
      return listFrom(data);
    },
    [request],
  );

  const fetchBursarySettings = useCallback(
    (params) => fetchList(Settings.urls.academics.bursarySettings, params),
    [fetchList],
  );
  const fetchGiantsSettings = useCallback(
    (params) => fetchList(Settings.urls.academics.giantsSettings, params),
    [fetchList],
  );
  const saveBursarySettings = useCallback(
    (data, id) =>
      request(
        id ? "PATCH" : "POST",
        id
          ? detailUrl(Settings.urls.academics.bursarySettingsDetail, id)
          : Settings.urls.academics.bursarySettings,
        data,
        id ? "Bursary settings updated" : "Bursary settings created",
      ),
    [request],
  );
  const saveGiantsSettings = useCallback(
    (data, id) =>
      request(
        id ? "PATCH" : "POST",
        id
          ? detailUrl(Settings.urls.academics.giantsSettingsDetail, id)
          : Settings.urls.academics.giantsSettings,
        data,
        id
          ? "Academic Giants settings updated"
          : "Academic Giants settings created",
      ),
    [request],
  );

  const fetchRankings = useCallback(
    (params) =>
      fetchList(Settings.urls.academics.bursaryGiantsRankings, params),
    [fetchList],
  );
  const triggerRankings = useCallback(
    (data) =>
      request(
        "POST",
        Settings.urls.academics.bursaryGiantsRankingsTrigger,
        data,
        "Ranking task queued",
      ),
    [request],
  );

  const fetchBursaryRecommendations = useCallback(
    (params) =>
      fetchList(Settings.urls.academics.bursaryRecommendations, params),
    [fetchList],
  );
  const fetchGiantsRecommendations = useCallback(
    (params) =>
      fetchList(Settings.urls.academics.giantsRecommendations, params),
    [fetchList],
  );
  const updateBursaryRecommendation = useCallback(
    (id, data) =>
      request(
        "PATCH",
        detailUrl(Settings.urls.academics.bursaryRecommendationDetail, id),
        data,
        "Recommendation updated",
      ),
    [request],
  );
  const updateGiantsRecommendation = useCallback(
    (id, data) =>
      request(
        "PATCH",
        detailUrl(Settings.urls.academics.giantsRecommendationDetail, id),
        data,
        "Recommendation updated",
      ),
    [request],
  );

  const bulkBursaryReview = useCallback(
    (ids) =>
      request(
        "POST",
        Settings.urls.academics.bursaryRecommendationsReview,
        { ids },
        "Recommendations reviewed",
      ),
    [request],
  );
  const bulkBursaryReject = useCallback(
    (ids, rejectionReason) =>
      request(
        "POST",
        Settings.urls.academics.bursaryRecommendationsReject,
        { ids, rejectionReason },
        "Recommendations rejected",
      ),
    [request],
  );
  const bulkBursarySubmit = useCallback(
    (ids) =>
      request(
        "POST",
        Settings.urls.academics.bursaryRecommendationsSubmit,
        { ids },
        "Recommendations submitted",
      ),
    [request],
  );
  const bulkGiantsReview = useCallback(
    (ids) =>
      request(
        "POST",
        Settings.urls.academics.giantsRecommendationsReview,
        { ids },
        "Recommendations reviewed",
      ),
    [request],
  );
  const bulkGiantsApprove = useCallback(
    (ids, awardChoice) =>
      request(
        "POST",
        Settings.urls.academics.giantsRecommendationsApprove,
        { ids, awardChoice },
        "Recommendations approved",
      ),
    [request],
  );
  const bulkGiantsReject = useCallback(
    (ids, rejectionReason) =>
      request(
        "POST",
        Settings.urls.academics.giantsRecommendationsReject,
        { ids, rejectionReason },
        "Recommendations rejected",
      ),
    [request],
  );

  const fetchAllocations = useCallback(
    (params) => fetchList(Settings.urls.academics.bursaryAllocations, params),
    [fetchList],
  );
  const fetchDashboard = useCallback(
    async (params) => {
      const qs = query(params);
      return request(
        "GET",
        `${Settings.urls.academics.bursaryAllocationsDashboard}${qs ? `?${qs}` : ""}`,
        null,
      );
    },
    [request],
  );
  const bulkAllocationApprove = useCallback(
    (ids) =>
      request(
        "POST",
        Settings.urls.academics.bursaryAllocationsApprove,
        { ids },
        "Allocations approved",
      ),
    [request],
  );
  const bulkAllocationReject = useCallback(
    (ids, rejectionReason) =>
      request(
        "POST",
        Settings.urls.academics.bursaryAllocationsReject,
        { ids, rejectionReason },
        "Allocations rejected",
      ),
    [request],
  );
  const bulkAllocationSendToFinance = useCallback(
    (ids) =>
      request(
        "POST",
        Settings.urls.academics.bursaryAllocationsFinance,
        { ids },
        "Allocations sent to finance",
      ),
    [request],
  );
  const bulkAllocationProcessed = useCallback(
    (ids) =>
      request(
        "POST",
        Settings.urls.academics.bursaryAllocationsProcessed,
        { ids },
        "Allocations marked processed",
      ),
    [request],
  );

  const fetchNotificationLogs = useCallback(
    (params) => fetchList(Settings.urls.academics.bursaryNotifications, params),
    [fetchList],
  );
  const resendNotifications = useCallback(
    (allocationIds) =>
      request(
        "POST",
        Settings.urls.academics.bursaryNotificationsResend,
        { allocation_ids: allocationIds },
        "Notification emails queued for resend",
      ),
    [request],
  );

  return {
    loading,
    fetchBursarySettings,
    fetchGiantsSettings,
    saveBursarySettings,
    saveGiantsSettings,
    fetchRankings,
    triggerRankings,
    fetchBursaryRecommendations,
    fetchGiantsRecommendations,
    updateBursaryRecommendation,
    updateGiantsRecommendation,
    bulkBursaryReview,
    bulkBursaryReject,
    bulkBursarySubmit,
    bulkGiantsReview,
    bulkGiantsApprove,
    bulkGiantsReject,
    fetchAllocations,
    fetchDashboard,
    bulkAllocationApprove,
    bulkAllocationReject,
    bulkAllocationSendToFinance,
    bulkAllocationProcessed,
    fetchNotificationLogs,
    resendNotifications,
  };
};

export default useBursaryGiants;
